import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import sidebarLogo from '@/assets/sidebar-logo.png'
import { cn, avatarColor } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { queryClient } from '@/main'
import { Tooltip } from '@/components/ui/tooltip'
import { LogOut } from 'lucide-react'
import { MAIN_NAV_ROUTES, ADMIN_NAV_ROUTES, SETTINGS_NAV_ROUTE, type NavRoute } from './navRoutes'

export function CommandSidebar() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  const { logout, user } = useAuthStore()

  // Cast t to accept any string key — navRoutes uses dynamic keys not in the strict union
  const tAny = t as (k: string) => string

  /** Resolve a route label — literal overrides i18n key. */
  const resolveLabel = (r: NavRoute) => r.labelLiteral ?? tAny(r.labelKey)

  const handleLogout = () => {
    queryClient.clear()
    logout()
  }

  const initials = user?.username
    ? user.username.slice(0, 2).toUpperCase()
    : '??'
  const avatarBg = user?.username ? avatarColor(user.username) : 'bg-muted'

  const navLink = (r: NavRoute) => {
    const label = resolveLabel(r)
    const isActive = location.pathname === r.href
    return (
      <Tooltip key={r.href} content={label} side="right" delayDuration={150}>
        <Link
          to={r.href}
          onClick={() => setSidebarOpen(false)}
          className={cn(
            'relative mx-auto flex h-10 w-10 items-center justify-center rounded-sm transition-colors duration-100',
            isActive
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          )}
        >
          {/* Amber left accent bar */}
          {isActive && (
            <span className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 bg-primary" />
          )}
          <r.Icon className="h-5 w-5" />
        </Link>
      </Tooltip>
    )
  }

  // Mobile: icon+label list rows (w-64 panel — touch tooltips don't work)
  const mobileNavLink = (r: NavRoute) => {
    const label = resolveLabel(r)
    const isActive = location.pathname === r.href
    return (
      <Link
        key={r.href}
        to={r.href}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          'relative flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-100',
          isActive
            ? 'text-primary bg-accent/40'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
        )}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 bg-primary" />
        )}
        <r.Icon className="h-5 w-5" />
        <span>{label}</span>
      </Link>
    )
  }

  return (
    <>
      {/* Desktop: fixed icon rail w-16 */}
      <aside
        data-testid="command-rail"
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-16 bg-card border-r border-border flex flex-col',
          // Hidden on mobile — mobile panel handles it
          'hidden lg:flex'
        )}
      >
        {/* Logo mark — constrained to fit the w-16 rail */}
        <Link
          to="/"
          className="flex h-14 items-center justify-center border-b border-border flex-shrink-0"
        >
          <img
            src={sidebarLogo}
            alt="DeterCam"
            className="h-6 w-auto max-w-[48px] object-contain block"
          />
        </Link>

        {/* Nav icons */}
        <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-1">
          {/* Main nav */}
          <div className="flex flex-col gap-1">
            {MAIN_NAV_ROUTES.map(r => navLink(r))}
          </div>

          {/* Admin section separator + admin items */}
          <div className="mt-4 flex flex-col gap-1">
            <div className="my-1 mx-3 border-t border-border/40" />
            {ADMIN_NAV_ROUTES.map(r => user?.is_superuser ? navLink(r) : null)}
            {navLink(SETTINGS_NAV_ROUTE)}
          </div>
        </nav>

        {/* Bottom: avatar initials + logout icon */}
        <div className="border-t border-border flex flex-col items-center gap-2 py-3 flex-shrink-0">
          {user && (
            <Tooltip content={user.username} side="right" delayDuration={150}>
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold text-white cursor-default',
                  avatarBg
                )}
              >
                {initials}
              </div>
            </Tooltip>
          )}
          <Tooltip content={t('sidebar.logout')} side="right" delayDuration={150}>
            <button
              onClick={handleLogout}
              className="flex h-10 w-10 items-center justify-center rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-100 mx-auto"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </aside>

      {/* Mobile: slide-in panel (icon + label rows, w-64) — same overlay pattern as upstream */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-[100dvh] w-64 bg-card border-r border-border flex flex-col',
          'lg:hidden',
          !sidebarOpen && '-translate-x-full',
          'transition-transform duration-300'
        )}
      >
        {/* Logo */}
        <div className="flex h-14 items-center border-b border-border px-4 flex-shrink-0">
          <Link to="/" onClick={() => setSidebarOpen(false)}>
            <img
              src={sidebarLogo}
              alt="DeterCam"
              className="h-8 w-auto object-contain block"
            />
          </Link>
        </div>

        {/* Mobile nav — icon + label rows */}
        <nav className="flex-1 overflow-y-auto p-2 flex flex-col gap-0.5">
          <div className="flex flex-col gap-0.5">
            {MAIN_NAV_ROUTES.map(r => mobileNavLink(r))}
          </div>
          <div className="pt-3 flex flex-col gap-0.5">
            <p className="px-3 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground/60">
              {t('sidebar.admin')}
            </p>
            {ADMIN_NAV_ROUTES.map(r => user?.is_superuser ? mobileNavLink(r) : null)}
            {mobileNavLink(SETTINGS_NAV_ROUTE)}
          </div>
        </nav>

        {/* Mobile bottom: avatar + logout */}
        <div className="border-t border-border p-3 flex items-center gap-2.5 flex-shrink-0">
          {user && (
            <div
              className={cn(
                'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white',
                avatarBg
              )}
            >
              {initials}
            </div>
          )}
          {user && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium leading-tight">{user.username}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-100 flex-shrink-0"
            title={t('sidebar.logout')}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>
    </>
  )
}
